import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { MappedStakeholder, StakeholderRoleEnum } from "src/core/constants/folder";
import { useFolderCorporationId } from "src/core/contexts/FolderContext";
import { useDeleteStakeholderMutation } from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";

type UseDeleteStakeholderProps = {
  stakeholder?: MappedStakeholder;
  onSuccess?: () => void;
};

/**
 * Hook permettant de gérer la suppression d'un stakeholder.
 */
export function useDeleteStakeholder({ stakeholder, onSuccess }: UseDeleteStakeholderProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  // Permet de récupérer l'id de la société du dossier
  const corporationId = useFolderCorporationId();

  const [deleteStakeholder, { loading }] = useDeleteStakeholderMutation();

  const handleDelete = useCallback(async () => {
    if (!stakeholder || !corporationId) return;

    try {
      await deleteStakeholder({
        variables: {
          sourceEntityId: stakeholder.id,
          targetEntityId: corporationId,
          isLegalRep: stakeholder.roles.includes(StakeholderRoleEnum.legal_representative),
          isDirectBO: stakeholder.roles.includes(StakeholderRoleEnum.direct_beneficiary_owner),
          roleTypeCode_1: stakeholder.roles.includes(StakeholderRoleEnum.legal_representative)
            ? "legal_representative"
            : null,
          roleTypeCode_2: stakeholder.roles.includes(StakeholderRoleEnum.direct_beneficiary_owner)
            ? "direct_beneficiary_owner"
            : null,
        },
      });

      toast({
        description: t("modal.deleteStakeholder.success"),
      });

      onSuccess?.();
    } catch (error) {
      toast({
        variant: "destructive",
        description: t("modal.deleteStakeholder.error"),
      });
    }
  }, [stakeholder, corporationId, deleteStakeholder, t, toast, onSuccess]);

  return {
    corporationId,
    handleDelete,
    loading,
  };
}
